import { useI18n } from "../i18n/index.tsx"
import { pageNumbers } from "../lib/pagination.ts"
import { IconButton } from "./Button.tsx"
import { Select } from "./Field.tsx"

/** 한 쪽에 몇 건. 100 을 넘기면 창고 PC 에서 그리는 데만 한참 걸린다. */
export const PAGE_SIZES = [20, 50, 100] as const

/**
 * 목록 아래 페이지 줄. 쪽 번호를 직접 누를 수 있어야 한다 —
 * "이전/다음"만 있으면 200쪽짜리 목록에서 어제 본 자리로 돌아갈 방법이 없다.
 * 어떤 번호를 보여 줄지는 `lib/pagination.ts` 가 정한다(테스트가 지킨다).
 */
export function Pagination({
  page,
  size,
  total,
  onPage,
  onSize,
}: {
  page: number
  size: number
  total: number
  onPage: (page: number) => void
  onSize: (size: number) => void
}) {
  const { t } = useI18n()
  const lastPage = Math.max(1, Math.ceil(total / size))
  const from = total === 0 ? 0 : (page - 1) * size + 1
  const to = Math.min(total, page * size)

  return (
    <div className="pager">
      <span className="muted">{t("cases.range", { from, to, total })}</span>

      <div className="pager__nav">
        <IconButton
          icon="chevronLeft"
          label={t("cases.prev")}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        />
        {pageNumbers(page, lastPage).map((entry, index) =>
          entry === "gap" ? (
            <span key={`gap-${index}`} className="muted">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              className={entry === page ? "pager__page is-current" : "pager__page"}
              aria-current={entry === page ? "page" : undefined}
              onClick={() => onPage(entry)}
            >
              {entry}
            </button>
          ),
        )}
        <IconButton
          icon="chevronRight"
          label={t("cases.next")}
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
        />
      </div>

      <Select
        className="pager__size"
        value={String(size)}
        aria-label={t("cases.pageSize")}
        onChange={(event) => onSize(Number(event.target.value))}
      >
        {PAGE_SIZES.map((option) => (
          <option key={option} value={option}>
            {t("cases.perPage", { count: option })}
          </option>
        ))}
      </Select>
    </div>
  )
}
